import { Button, Dropdown, Table } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbDotsVertical } from 'react-icons/tb'

import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { formatBytes, personName } from '../Helpers/diskFormat'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { DiskNodeIcon } from './DiskNodeIcon'
import { useDiskActions } from './useDiskActions'

import type { DiskNode } from '../Contracts/DiskNode'
import type { TableProps } from 'antd'

import styles from './Disk.module.css'

/**
 * Кошик простору: лише корені операцій «у кошик» (папка — разом із вмістом),
 * звідки видалено, хто й коли, розмір; «Відновити» і «Видалити назавжди».
 */
export const DiskTrashTable = observer(({ compact }: { compact: boolean }) => {
    const actions = useDiskActions()

    const from = (node: DiskNode): string => node.originalParentName ?? '—'

    const columns: NonNullable<TableProps<DiskNode>['columns']> = [
        {
            key: 'name',
            title: 'Назва',
            render: (_: unknown, node) => (
                <span className={classNames(styles['nameCell'])}>
                    <DiskNodeIcon kind={node.kind} size={30} />
                    <span className={classNames(styles['nameText'])}>
                        <span className={classNames(styles['name'])} title={node.name}>
                            {node.name}
                        </span>
                        {compact && (
                            <span className={classNames(styles['sub'])}>
                                з «{from(node)}» · {formatDateTimeUk(node.deletedAt)}
                            </span>
                        )}
                    </span>
                </span>
            ),
        },
        {
            key: 'from',
            title: 'Звідки видалено',
            width: 220,
            render: (_: unknown, node) => <span className={classNames(styles['muted'])}>{from(node)}</span>,
        },
        {
            key: 'deleted',
            title: 'Видалено',
            width: 220,
            render: (_: unknown, node) => (
                <span className={classNames(styles['nameText'])}>
                    <span className={classNames(styles['muted'])}>{formatDateTimeUk(node.deletedAt)}</span>
                    <span className={classNames(styles['sub'])}>{personName(node.deletedBy)}</span>
                </span>
            ),
        },
        {
            key: 'size',
            title: 'Розмір',
            width: 110,
            align: 'right',
            render: (_: unknown, node) => (
                <span className={classNames(styles['muted'])}>
                    {node.type === 'file' ? formatBytes(node.size) : '—'}
                </span>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: 48,
            align: 'right',
            render: (_: unknown, node) => {
                const items = actions.trashMenu(node)

                return items.length > 0 ? (
                    <span onClick={(event) => event.stopPropagation()}>
                        <Dropdown trigger={['click']} menu={{ items }} placement="bottomRight">
                            <Button
                                type="text"
                                size="small"
                                icon={<TbDotsVertical />}
                                aria-label={`Дії: ${node.name}`}
                            />
                        </Dropdown>
                    </span>
                ) : null
            },
        },
    ]

    return (
        <Table<DiskNode>
            className={classNames(styles['table'])}
            size="middle"
            rowKey="uuid"
            columns={compact ? columns.filter((column) => column.key === 'name' || column.key === 'actions') : columns}
            dataSource={store.items}
            loading={store.isLoading}
            pagination={false}
            locale={{ emptyText: ' ' }}
            showHeader={!compact}
            tableLayout="fixed"
            rowSelection={{
                selectedRowKeys: store.selected,
                onChange: (keys) => store.setSelected(keys.map(String)),
                columnWidth: compact ? 36 : 44,
            }}
            onRow={(node) => ({
                onClick: (event) =>
                    store.click(node.uuid, { ctrl: event.ctrlKey || event.metaKey, shift: event.shiftKey }),
            })}
            data-testid="disk-trash"
        />
    )
})
